import React from 'react';
import type { PurchaseOrder } from '../purchaseOrdersTypes';
import styles from './POTable.module.css';

interface POTableProps {
  orders: PurchaseOrder[];
  busyId?: string | null;
  onApprove: (order: PurchaseOrder) => void;
  onReject: (order: PurchaseOrder) => void;
  onSend: (order: PurchaseOrder) => void;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'statusDraft' },
  pending_approval: { label: 'Pending Approval', className: 'statusPendingApproval' },
  approved: { label: 'Approved', className: 'statusApproved' },
  sent: { label: 'Sent', className: 'statusSent' },
  transit: { label: 'Transit', className: 'statusTransit' },
  partial: { label: 'Partial', className: 'statusPartial' },
  delayed: { label: 'Delayed', className: 'statusDelayed' },
  done: { label: 'Done', className: 'statusDone' },
};

const getProgressColor = (status: string, progress: number): string => {
  if (status === 'done') return '#22C55E';
  if (status === 'delayed') return '#EF4444';
  if (status === 'sent') return '#EF4444';
  if (progress >= 80) return '#22C55E';
  if (progress >= 50) return '#F59E0B';
  if (progress >= 30) return '#3B82F6';
  return '#E5E7EB';
};

export const POTable: React.FC<POTableProps> = ({ orders, busyId, onApprove, onReject, onSend }) => {
  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>PO Number</th>
            <th>Vendor</th>
            <th>Warehouse</th>
            <th>Created</th>
            <th>Expected</th>
            <th>Items</th>
            <th>Total</th>
            <th>Progress</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const status = statusConfig[order.status];
            const progressColor = getProgressColor(order.status, order.progress);
            const busy = busyId === order.id;
            // VP-ADM-003: draft/pending_approval need an Admin's Approve or
            // Reject before they can be sent; approved is the only status
            // that can move to sent.
            const needsApproval = order.backendStatus === 'draft' || order.backendStatus === 'pending_approval';
            const canSend = order.backendStatus === 'approved';

            return (
              <tr key={order.id} className={styles.row}>
                <td>
                  <span className={styles.poNumber}>{order.poNumber}</span>
                </td>
                <td>{order.vendor}</td>
                <td>{order.warehouse}</td>
                <td className={styles.dateCell}>{order.created}</td>
                <td className={styles.dateCell}>{order.expected}</td>
                <td className={styles.numberCell}>{order.items}</td>
                <td className={styles.numberCell}>
                  ${order.total.toLocaleString()}
                </td>
                <td>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${order.progress}%`,
                        background: progressColor,
                      }}
                    />
                  </div>
                </td>
                <td>
                  <span className={`${styles.badge} ${styles[status.className]}`}>
                    {status.label}
                  </span>
                </td>
                <td>
                  <div className={styles.actionGroup}>
                    {needsApproval && (
                      <>
                        <button
                          className={`${styles.actionBtn} ${styles.actionApprove}`}
                          onClick={() => onApprove(order)}
                          disabled={busy}
                          title="Approve this purchase order so it can be sent to the supplier"
                        >
                          {busy ? 'Working…' : 'Approve'}
                        </button>
                        <button
                          className={`${styles.actionBtn} ${styles.actionReject}`}
                          onClick={() => onReject(order)}
                          disabled={busy}
                          title="Reject and cancel this purchase order"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {canSend && (
                      <button
                        className={`${styles.actionBtn} ${styles.actionSend}`}
                        onClick={() => onSend(order)}
                        disabled={busy}
                        title="Send this approved purchase order to the supplier"
                      >
                        {busy ? 'Working…' : 'Send'}
                      </button>
                    )}
                    {!needsApproval && !canSend && (
                      <span className={styles.actionNone}>—</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {orders.length === 0 && (
        <div className={styles.empty}>No purchase orders match the current filters.</div>
      )}
    </div>
  );
};
